import { useEffect, useState } from "react";
import Router from "next/router";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import ReactGA from "react-ga";
import { useQuery } from "@apollo/client";
import Header from "./header/Header";
import Footer from "./footer/Footer";
import styles from "./layouts.module.scss";
import config from "../../common/config";
import {
  buildFavListingsObj,
  getLastFavIDsSync,
  getPathLevel,
  getStoredFavListingIDs,
  storeFavListingIDs,
} from "../../lib/helpers";
import { URL_LEVELS } from "../../common/constants";
import { useAuthState } from "../../contexts/auth/auth.provider";
import { GET_FAV_IDS } from "../../graphql/queries/get-favs";
import { GET_NEW_MSGS_COUNT } from "../../graphql/queries/user";
import { useFavListingsDispatch } from "../../contexts/fav-listings/favListings.provider";
import actions from "../../contexts/fav-listings/favListings.actions";

import { useAppState } from "@/contexts/app/app.provider";
import UserSidebarV2 from "@/containers/layouts/sidebar/UserSidebarV2";

const { setFavListings } = actions;

const UserSidebar = dynamic(() => import("./sidebar/UserSidebar"), {
  ssr: false,
});

const SearchBar = dynamic(() => import("./searchbar/searchbar"), {
  ssr: false,
});

// TODO: remove the dynamic import for SEO purposes for services popup
const ServicesPopup = dynamic(() => import("./popup/ServicesPopup"), {
  ssr: false,
});

const ONE_MINUTE = 60000 * 10;

const AppLayout = ({ hideFooter, children, type }) => {
  const dispatch = useFavListingsDispatch();
  const { token } = useAuthState();
  const { updateContext: updateAppContext } = useAppState();
  const [isGALoaded, setIsGALoaded] = useState(false);
  const [skipGetFavIDs, setSkipGetFavIDs] = useState(true);
  useQuery(GET_FAV_IDS, {
    skip: !token || skipGetFavIDs,
    context: { token },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      const favListingIDs = data.User.getFavIDs;
      const favListingsObject = buildFavListingsObj(favListingIDs, Date.now());
      storeFavListingIDs(favListingsObject);
      //updateAppContext({ favListingsObject, favListingIDs });
    },
  });

  useQuery(GET_NEW_MSGS_COUNT, {
    context: { token },
    skip: !token,
    fetchPolicy: "no-cache",
    pollInterval: ONE_MINUTE,
    onCompleted: (data) => {
      const newMsgs = (data?.Chat?.msgs?.list || [])[0]?.new_msgs;
      updateAppContext({ newMsgs: newMsgs });
    },
  });

  const handleRouteChangeComplete = () => {
    const { asPath: url, query } = Router;
    const { params } = query;

    ReactGA.set({ page: url });
    ReactGA.pageview(url);

    // handle open details page
    if (params && getPathLevel(params)?.level === URL_LEVELS.LISTING) {
      ReactGA.event({
        category: "open_details_page",
        action: "opened details page",
      });
    }

    // Handle filter by price event
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.has("price_range")) {
      ReactGA.event({
        category: "filter_by_price",
        action: "filter listings by price range",
      });
    }
  };

  useEffect(() => {
    const ONE_HOUR = 60 * 60 * 1000;
    const lastSyncDate = getLastFavIDsSync();
    const skip = !token || Date.now() - lastSyncDate < ONE_HOUR;
    setSkipGetFavIDs(skip);
  }, [token]);

  useEffect(() => {
    if (token) {
      const storedFavListingsObject = getStoredFavListingIDs();
      if (storedFavListingsObject) {
        dispatch(setFavListings(storedFavListingsObject));
      }
    } else {
      storeFavListingIDs({});
      dispatch(setFavListings({}));
    }
  }, [token]);

  useEffect(() => {
    if (!isGALoaded) {
      ReactGA.initialize(config.GA_TRACKING_ID);

      if (window.location.search === "") {
        // This is because of a bug in Next.js as it does not fire routeChangeComplete on first load unless there is
        // query params. We do as so to prevent double count on GA.
        handleRouteChangeComplete();
      }
      setIsGALoaded(true);
    }

    Router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      Router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, []);

  return (
    <div dir="rtl" className={styles.layoutWrapper}>
      {/*<UserSidebar />*/}
      <UserSidebarV2 />
      <ServicesPopup />
      <Header />
      {children}
      {type !== "no-footer" && !hideFooter && <Footer />}
    </div>
  );
};

AppLayout.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string,
  isDailyRentPage: PropTypes.bool,
  isRedMode: PropTypes.bool,
};

AppLayout.defaultProps = {
  children: null,
  type: undefined,
  isDailyRentPage: false,
  isRedMode: false,
};

export default AppLayout;
